import React from 'react';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

import classNames from 'classnames';
import MessagePropType from '../definitions/MessagePropType';
import Typography from '../definitions/Typography';
import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import ZIndex from '../definitions/ZIndex';
import messageToString from '../functions/messageToString';
import IconChevronDown from './icons/IconChevronDown';
import { SIZES as PARAGRAPH_SIZES } from './Paragraph';
import AccordionBase from './AccordionBase';
import MessageBanner from './MessageBanner';


function AccordionCalculation({
    isExpanded,
    onClick,
    isInitiallyExpanded,

    title,
    subtitle,

    children,
    'data-name': dataName,

    messageBannerProps,
    shouldAlwaysShowMessageBanner,

    badge,
    disabled,

    onAnimationEnd,
    className,

    ...otherProps
}) {
    return (
        <AccordionBase
            isExpanded={isExpanded}
            isInitiallyExpanded={isInitiallyExpanded}
            onClick={onClick}

            HeaderComponent={StyledAccordionCalculationHeader}
            headerProps={{
                title,
                subtitle,
                'data-name': dataName,
                messageBannerProps,
                shouldAlwaysShowMessageBanner,
                badge,
            }}

            alwaysRenderChildMarkup={true}
            disabled={disabled}
            onAnimationEnd={onAnimationEnd}
            className={classNames(
                'rh-mb-0_5 rh-bg-coconut rh-border-radius-6px ',
                className,
            )}
            {...otherProps}
        >
            <div className={classNames(
                'accordion-child-container rh-py-2 rh-border-style-solid',
                isExpanded ? 'rh-border-color-stone' : 'rh-border-color-transparent',
            )}
            >
                {children}
            </div>
        </AccordionBase>
    );
}


AccordionCalculation.propTypes = {
    isExpanded: PropTypes.bool,
    onClick: PropTypes.func,
    isInitiallyExpanded: PropTypes.bool,

    title: MessagePropType.isRequired,
    subtitle: MessagePropType,

    children: PropTypes.node.isRequired,
    'data-name': PropTypes.string,

    messageBannerProps: PropTypes.shape({
        variant: PropTypes.string.isRequired,
        iconKey: PropTypes.string,
        iconSize: PropTypes.oneOf([
            Sizes.ICONS.S,
            Sizes.ICONS.L,
        ]),
        message: MessagePropType.isRequired,
        messageSize: PropTypes.oneOf(
            Object.values(PARAGRAPH_SIZES),
        ),
    }),
    shouldAlwaysShowMessageBanner: PropTypes.bool,

    badge: PropTypes.number,
    disabled: PropTypes.bool,
    onAnimationEnd: PropTypes.func,
    className: PropTypes.string,
};

AccordionCalculation.defaultProps = {
    isExpanded: undefined,
    onClick: undefined,
    isInitiallyExpanded: false,

    subtitle: undefined,

    'data-name': 'AccordionCalculation',

    messageBannerProps: undefined,
    shouldAlwaysShowMessageBanner: false,

    badge: undefined,
    disabled: false,
    onAnimationEnd: undefined,
    className: undefined,
};

/* Disabled lint errors due to creating AccordionCalculationHeader in this file */
/* eslint-disable react/no-multi-comp */
function AccordionCalculationHeader({
    isExpanded,
    onClick,

    title,
    subtitle,

    'data-name': dataName,

    shouldAlwaysShowMessageBanner,
    messageBannerProps,
    className,
    disabled,
    badge,

    ...otherProps
}) {
    const intl = useIntl();

    // Message banner is visible only when collapsed unless overridden
    const isMessageBannerVisible = (messageBannerProps && (shouldAlwaysShowMessageBanner || !isExpanded));

    return (
        <div
            role="button"
            tabIndex="0"
            onClick={onClick}
            /* Accordions should be toggled with either Enter or Space */
            onKeyPress={(e) => (e?.key === 'Enter' || e?.key === ' ') && onClick(e)}
            data-name={dataName}
            className={classNames(
                'rh-position-relative rh-display-flex rh-mx-0 rh-my-0 rh-cursor-pointer rh-py-1',
                badge ? 'rh-px-2' : 'rh-px-0',
                className,
            )}

            {...otherProps}
        >
            <div className="header-content rh-display-flex rh-position-relative">
                <div className="title-container">
                    <h3 className={classNames(
                        'accordion-title rh-title-xs weight-regular rh-my-0',
                        { 'rh-fg-stone': disabled },
                    )}
                    >
                        {messageToString(title, intl)}
                    </h3>

                    <If condition={subtitle}>
                        <h4 className="accordion-subtitle rh-title-2xl rh-my-0 rh-fg-blueberry-dark">
                            {messageToString(subtitle, intl)}
                        </h4>
                    </If>
                </div>

                <IconChevronDown
                    className="accordion-chevron rh-ml-auto rh-icon-m"
                    stroke={Colours.BLACKBERRY}
                    strokeWidth="3px"
                    outlineWidth="0"
                />
            </div>

            <If condition={isMessageBannerVisible}>
                <div className="accordion-message-banner rh-mt-2">
                    <MessageBanner
                        {...messageBannerProps}
                    />
                </div>
            </If>
        </div>
    );
}

AccordionCalculationHeader.propTypes = {
    isExpanded: PropTypes.bool,
    onClick: PropTypes.func,

    title: MessagePropType.isRequired,
    subtitle: MessagePropType,

    'data-name': PropTypes.string,

    messageBannerProps: PropTypes.shape({
        variant: PropTypes.string.isRequired,
        iconKey: PropTypes.string,
        iconSize: PropTypes.oneOf([
            Sizes.ICONS.S,
            Sizes.ICONS.L,
        ]),
        message: MessagePropType.isRequired,
        messageSize: PropTypes.oneOf(
            Object.values(PARAGRAPH_SIZES),
        ),
    }),
    shouldAlwaysShowMessageBanner: PropTypes.bool,

    badge: PropTypes.number,
    disabled: PropTypes.bool,
    className: PropTypes.string,
};

AccordionCalculationHeader.defaultProps = {
    isExpanded: undefined,
    onClick: undefined,

    subtitle: undefined,

    'data-name': 'AccordionCalculation',

    messageBannerProps: undefined,
    shouldAlwaysShowMessageBanner: false,

    badge: undefined,
    disabled: false,
    className: undefined,
};


const TRANSITION_TIMING_LONG = '0.4s';

const StyledAccordionCalculation = styled(observer(AccordionCalculation))`
    box-shadow: 0 2px 11px 0 ${Colours.STONE};

    padding: 0 5% 0;

    .accordion-child-container {
        border-width: 1px 0 0 0;
    }
`;

/* This has to be a separate component cause we get some of our props from AccordionBase */
const StyledAccordionCalculationHeader = styled(AccordionCalculationHeader)`
    flex: 1;
    flex-direction: column;

    > .header-content {
        flex: 1;
        align-items: center;

        > .accordion-chevron {
            flex-shrink: 0;

            transition: transform ${TRANSITION_TIMING_LONG};

            ${props => props.isExpanded && `
                transform: rotate(180deg);
            `}

            ${props => props.disabled && `
                stroke: transparent;
            `}
        }
    }

    ${props => props.badge && `
        > .header-content > .title-container {
            &::before {
                content: "${props.badge}";
                display: flex;
                align-items: center;
                justify-content: center;

                position: absolute;
                right: calc(100% + ${Sizes.SPACING.ONE});
                top: 50%;
                transform: translateY(-50%);

                z-index: ${ZIndex.ELEMENTS};

                width: ${Sizes.SPACING.TWO};
                height: ${Sizes.SPACING.TWO};
                border-radius: 50%;

                font-family: ${Typography.FONT_FAMILY_STACK};
                font-weight: ${Typography.WEIGHTS.MEDIUM};

                background-color: ${props.disabled ? `${Colours.STONE}` : `${Colours.BLUEBERRY_LIGHT}`};
                color: ${props.disabled ? `${Colours.COCONUT}` : `${Colours.BLUEBERRY_DARK}`};
            }
        }
    `}
`;

export default StyledAccordionCalculation;
